import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { TranslationService } from '@trimble-gcs/i18n';
import { PluralArgs } from '@trimble-gcs/i18n-types';
import { ModusButtonModule, ModusIconModule, ModusTooltipModule } from '@trimble-gcs/modus';
import { SetClassificationForEdit } from '../../../../classification/classification-scheme.actions';
import { ClassificationScheme } from '../../../../classification/classification-scheme.model';
import { Classification } from '../../../../classification/classification.model';
import { ClassificationService } from '../../../../classification/classification.service';
import { PercentagePipe } from '../../../../pipes/percentage.pipe';
import { ScandataModel } from '../../../../scandata/scandata.models';
import { SetView } from '../../../options-panel.actions';
import { OptionsPanelView } from '../../../options-panel.models';
import { ClassificationGraphComponent } from './classification-graph/classification-graph.component';
import { classificationTranslations } from './classification.translations';

@UntilDestroy()
@Component({
  selector: 'sd-classification',
  imports: [
    CommonModule,
    ClassificationGraphComponent,
    PercentagePipe,
    ModusTooltipModule,
    ModusButtonModule,
    ModusIconModule,
  ],
  templateUrl: './classification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClassificationComponent {
  private readonly classificationService = inject(ClassificationService);
  private readonly store = inject(Store);
  private readonly translationService = inject(TranslationService);

  readonly translations = classificationTranslations();

  readonly scandataModel = input.required<ScandataModel>();
  readonly readonly = input(false);

  private readonly classificationSchemes = toSignal(
    this.classificationService.getClassificationSchemes(),
    {
      initialValue: [],
    },
  );

  readonly classifications = this.getClassifications();
  readonly hasClassifications = computed(() => this.classifications().length > 0);

  readonly unallocatedCount = computed(
    () =>
      this.classificationService.getUnallocatedClassificationSchemes(
        this.classifications(),
        this.classificationSchemes(),
      ).length,
  );

  readonly showUnallocatedMessage = computed(() => this.unallocatedCount() > 0);

  readonly unallocatedMessage = computed(() => {
    const pluralArgs: PluralArgs = { count: this.unallocatedCount() };
    const pluralMap = this.translations.unallocatedClassifications;
    return this.translationService.translatePlural(pluralArgs, pluralMap);
  });

  readonly activeClassification = signal<Classification | null>(null);

  setActiveClassification(classification: Classification | null = null) {
    this.activeClassification.set(classification);
  }

  isActiveClassification(classification: Classification) {
    return classification === this.activeClassification();
  }

  editClassification(classificationScheme: ClassificationScheme) {
    this.store.dispatch([
      new SetClassificationForEdit(classificationScheme),
      new SetView(OptionsPanelView.ClassificationEdit, false),
    ]);
  }

  private getClassifications() {
    return computed(() => {
      const scan = this.scandataModel();
      const schemes = this.classificationSchemes();
      return scan && schemes
        ? this.classificationService.getScanClassifications(scan, schemes)
        : [];
    });
  }
}
